import { Fragment, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';

export default function MultiSelect({
    value = [],
    onChange,
    options,
    placeholder = 'ALL',
    className = '',
    disabled = false,
    maxChips = 1,
}) {
    const selected = useMemo(() => options.filter((o) => value.includes(o.value)), [options, value]);
    const isAllSelected = options.length > 0 && value.length === options.length;

    const clearAll = () => onChange?.([]);
    const selectAll = () => onChange?.(options.map((o) => o.value));
    const toggleAll = () => (isAllSelected ? clearAll() : selectAll());

    return (
        <Listbox value={value} onChange={onChange} multiple disabled={disabled}>
            <div className={`relative ${className}`}>
                <Listbox.Button className='flex w-full items-center justify-between h-[44px] rounded border border-gray-300 px-3 text-left bg-white overflow-hidden'>
                    <div className='flex overflow-hidden gap-1 items-center whitespace-nowrap'>
                        {selected.length === 0 && <span className='text-gray-500'>{placeholder}</span>}
                        {selected.slice(0, maxChips).map((opt) => (
                            <span
                                key={opt.value}
                                className='inline-flex items-center px-2 py-0.5 text-xs text-gray-800 bg-gray-100 rounded-full truncate max-w-[10rem] shrink-0'
                            >
                                {opt.label}
                            </span>
                        ))}
                        {selected.length > maxChips && (
                            <span className='text-xs text-gray-600 shrink-0'>+{selected.length - maxChips} more</span>
                        )}
                    </div>
                    <ChevronDownIcon className='w-4 h-4 opacity-70' />
                </Listbox.Button>

                <Transition
                    as={Fragment}
                    leave='transition ease-in duration-100'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                >
                    <Listbox.Options className='overflow-auto absolute z-30 py-1 mt-1 w-full max-h-72 bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg focus:outline-none'>
                        <div className='flex gap-2 justify-between items-center px-2 pb-1'>
                            <button
                                type='button'
                                className='px-2 py-1 text-xs rounded hover:bg-gray-100'
                                onClick={toggleAll}
                            >
                                {isAllSelected ? 'Unselect all' : 'Select all'}
                            </button>
                            <button type='button' className='px-2 py-1 text-xs rounded hover:bg-gray-100' onClick={clearAll}>
                                Clear
                            </button>
                        </div>

                        {options.map((opt) => (
                            <Listbox.Option
                                key={opt.value}
                                value={opt.value}
                                className={({ active }) =>
                                    `relative cursor-pointer select-none px-3 py-2 text-sm ${active ? 'bg-gray-100' : ''}`
                                }
                            >
                                {({ selected }) => (
                                    <div className='flex gap-2 items-center'>
                                        <input
                                            type='checkbox'
                                            readOnly
                                            checked={selected}
                                            className='w-4 h-4 rounded border-gray-300'
                                        />
                                        <span className='flex-1 truncate'>{opt.label}</span>
                                        {selected && <CheckIcon className='w-4 h-4' aria-hidden='true' />}
                                    </div>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    );
}
